import { useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Trash2,
  Archive,
  RefreshCw,
  ChevronDown,
  X,
  ScanLine,
  Upload,
  List,
  Map,
  PackagePlus,
  ClipboardCheck,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/app/components/ui/collapsible';
import { Label } from '@/app/components/ui/label';
import {
  useAssets,
  useAssetMap,
  useBulkUpdateStatus,
  useDeleteAsset,
  useRestoreAsset,
  useUnretireAsset,
} from '@/hooks/use-assets';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgToday } from '@/hooks/use-settings';
import { toast } from '@/app/components/ui/simple-toaster';
import { useIsMobile } from '@/app/components/ui/use-mobile';
import { ScannerModal } from '@/components/scanner';
import { formatDateOnly } from '@/lib/date-utils';
import { handleApiError } from '@/lib/api-client';
import type { Asset, AssetStatus } from '@/lib/types';
import { AssetsMapView } from './assets-map-view';

const statusColors: Record<AssetStatus, string> = {
  active: 'bg-green-100 text-green-800',
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  in_use: 'bg-blue-100 text-blue-800',
  damaged: 'bg-orange-100 text-orange-800',
  retired: 'bg-gray-100 text-gray-800',
  lost: 'bg-red-100 text-red-800',
  quarantined: 'bg-red-100 text-red-800',
};

const serviceLifeColors: Record<string, string> = {
  expired: 'bg-red-100 text-red-800',
  expiring: 'bg-amber-100 text-amber-800',
  ok: 'bg-green-100 text-green-800',
};

const PAGE_SIZE = 20;

export function AssetsPage() {
  const orgToday = useOrgToday();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState<AssetStatus>('available');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanNotFoundDialogOpen, setScanNotFoundDialogOpen] = useState(false);
  const [scannedSerial, setScannedSerial] = useState('');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = (searchParams.get('status') || '') as AssetStatus | 'deleted' | '';
  const manufacturer = searchParams.get('manufacturer') || '';
  const dateAddedAfter = searchParams.get('date_added_after') || '';
  const dateAddedBefore = searchParams.get('date_added_before') || '';
  const lengthMin = searchParams.get('length_min') || '';
  const lengthMax = searchParams.get('length_max') || '';
  const swlMin = searchParams.get('swl_min') || '';
  const swlMax = searchParams.get('swl_max') || '';
  const q = searchParams.get('q') || '';
  const viewMode = searchParams.get('view') === 'map' ? 'map' : 'list';
  const isMapView = viewMode === 'map' && status !== 'deleted';

  const assetFilters = useMemo(
    () => ({
      q,
      status: status === 'deleted' ? '' : status,
      manufacturer,
      date_added_after: dateAddedAfter,
      date_added_before: dateAddedBefore,
      length_min: lengthMin,
      length_max: lengthMax,
      swl_min: swlMin,
      swl_max: swlMax,
      include_retired: status === 'retired' || undefined,
      include_deleted: status === 'deleted' || undefined,
      deleted_only: status === 'deleted' || undefined,
    }),
    [q, status, manufacturer, dateAddedAfter, dateAddedBefore, lengthMin, lengthMax, swlMin, swlMax]
  );

  const { data, isLoading, isError, refetch } = useAssets({
    page,
    page_size: PAGE_SIZE,
    ...assetFilters,
  });

  const {
    data: mapData,
    isLoading: isMapLoading,
    isError: isMapError,
    refetch: refetchMap,
  } = useAssetMap(assetFilters, isMapView);

  const bulkUpdateMutation = useBulkUpdateStatus();
  const deleteMutation = useDeleteAsset();
  const { canCreateAssets, canRecordInspections, isAdmin } = usePermissions();

  const assets = useMemo(() => data?.results ?? [], [data?.results]);
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(assets.map((a) => a.id));
      } else {
        setSelectedIds([]);
      }
    },
    [assets]
  );

  const handleSelectOne = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((i) => i !== id)));
  }, []);

  const handleBulkStatusUpdate = useCallback(async () => {
    if (selectedIds.length === 0) return;
    await bulkUpdateMutation.mutateAsync({
      asset_ids: selectedIds,
      status: selectedBulkStatus,
    });
    setSelectedIds([]);
    setBulkStatusDialogOpen(false);
  }, [selectedIds, selectedBulkStatus, bulkUpdateMutation]);

  const handleBulkDelete = useCallback(async () => {
    let successCount = 0;
    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await deleteMutation.mutateAsync(id);
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { error?: string } } };
        const msg = axiosErr?.response?.data?.error || `Failed to delete asset ${id}`;
        errors.push(msg);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} asset(s) deleted`);
    }
    if (errors.length > 0) {
      toast.error(`${errors.length} asset(s) failed to delete`, {
        description: errors.join('; '),
      });
    }
    setSelectedIds([]);
    setDeleteDialogOpen(false);
  }, [selectedIds, deleteMutation]);

  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    if (viewMode === 'map') {
      newParams.set('view', 'map');
    }
    setSearchParams(newParams);
    setSearchInput('');
  }, [setSearchParams, viewMode]);

  const handleScanResult = useCallback(
    (serialNumber: string) => {
      setScannedSerial(serialNumber);
      // Search for asset by serial number
      const matchingAsset = assets.find(
        (a) => a.serial_number.toLowerCase() === serialNumber.toLowerCase()
      );
      
      if (matchingAsset) {
        // Found - navigate to asset detail
        navigate(`/assets/${matchingAsset.id}`);
      } else {
        // Not found in current page - search via API
        setSearchInput(serialNumber);
        updateSearchParams({ q: serialNumber, page: '1' });
        // Show dialog offering to create new asset if not found after search
        setScanNotFoundDialogOpen(true);
      }
    },
    [assets, navigate, updateSearchParams]
  );

  const handleCreateFromScan = useCallback(() => {
    navigate(`/assets/new?serial=${encodeURIComponent(scannedSerial)}`);
    setScanNotFoundDialogOpen(false);
  }, [navigate, scannedSerial]);

  const hasActiveFilters =
    status || manufacturer || dateAddedAfter || dateAddedBefore || lengthMin || lengthMax || swlMin || swlMax;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Assets</h1>
          <p className="text-gray-600 mt-1">
            Manage all your equipment and inventory
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          {isMobile && (
            <Button variant="outline" onClick={() => setScannerOpen(true)}>
              <ScanLine className="h-4 w-4 mr-2" />
              Scan
            </Button>
          )}
          <Button variant="outline" onClick={() => (viewMode === 'map' ? refetchMap() : refetch())}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {isAdmin && (
            <Button variant="outline" asChild>
              <Link to="/assets/import">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Link>
            </Button>
          )}
          {canCreateAssets && (
            <Button variant="outline" asChild>
              <Link to="/assets/batch">
                <PackagePlus className="h-4 w-4 mr-2" />
                Receive gear
              </Link>
            </Button>
          )}
          {canRecordInspections ? (
            <Button variant="outline" asChild>
              <Link to="/inspections/batch">
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Batch inspect
              </Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled
              title="Requires Inspection Certified — an admin can enable it on your tech profile"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" />
              Batch inspect
            </Button>
          )}
          {canCreateAssets && (
            <Button asChild>
              <Link to="/assets/new">
                <Plus className="h-4 w-4 mr-2" />
                Add Asset
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by name, serial number, or manufacturer..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) => {
                setSelectedIds([]);
                updateSearchParams({
                  status: value === 'all' ? '' : value,
                  ...(value === 'deleted' ? { view: '' } : {}),
                });
              }}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="in_use">In Use</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
                <SelectItem value="quarantined">Quarantined</SelectItem>
              </SelectContent>
            </Select>
            <Collapsible open={showFilters} onOpenChange={setShowFilters}>
              <CollapsibleTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  More Filters
                  <ChevronDown
                    className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
            <div className="flex rounded-md border bg-white">
              <Button
                variant={!isMapView ? 'secondary' : 'ghost'}
                className="rounded-r-none"
                onClick={() => updateSearchParams({ view: '' })}
              >
                <List className="h-4 w-4 mr-2" />
                List
              </Button>
              <Button
                variant={isMapView ? 'secondary' : 'ghost'}
                className="rounded-l-none"
                disabled={status === 'deleted'}
                onClick={() => updateSearchParams({ view: 'map' })}
              >
                <Map className="h-4 w-4 mr-2" />
                Map
              </Button>
            </div>
            <Button onClick={handleSearch}>Search</Button>
          </div>

          <Collapsible open={showFilters} onOpenChange={setShowFilters}>
            <CollapsibleContent className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Manufacturer</Label>
                  <Input
                    placeholder="Filter by manufacturer"
                    value={manufacturer}
                    onChange={(e) => updateSearchParams({ manufacturer: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date Added (From)</Label>
                  <Input
                    type="date"
                    value={dateAddedAfter}
                    onChange={(e) => updateSearchParams({ date_added_after: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date Added (To)</Label>
                  <Input
                    type="date"
                    value={dateAddedBefore}
                    onChange={(e) => updateSearchParams({ date_added_before: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Length Range (m)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={lengthMin}
                      onChange={(e) => updateSearchParams({ length_min: e.target.value })}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={lengthMax}
                      onChange={(e) => updateSearchParams({ length_max: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>SWL Range (kg)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={swlMin}
                      onChange={(e) => updateSearchParams({ swl_min: e.target.value })}
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={swlMax}
                      onChange={(e) => updateSearchParams({ swl_max: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={clearFilters} className="text-sm">
                  <X className="h-4 w-4 mr-2" />
                  Clear all filters
                </Button>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {viewMode === 'list' && status !== 'deleted' && isAdmin && selectedIds.length > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.length} asset{selectedIds.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkStatusDialogOpen(true)}
              >
                Update Status
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isMapView ? (
        <Card>
          <CardContent className="p-0">
            <AssetsMapView
              data={mapData}
              isLoading={isMapLoading}
              isError={isMapError}
              onRefetch={() => refetchMap()}
            />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : isError ? (
              <div className="p-6 text-center text-red-600">
                Failed to load assets. Please try again.
              </div>
            ) : assets.length === 0 ? (
              <div className="p-12 text-center">
                <Archive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">
                  {q || hasActiveFilters ? 'No assets match your search' : 'No assets yet'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {q || hasActiveFilters
                    ? 'Try adjusting your search or filters'
                    : 'Add your first asset to start tracking your equipment inventory'}
                </p>
                <div className="flex items-center justify-center gap-3 mt-4">
                  {(q || hasActiveFilters) && (
                    <Button variant="outline" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-2" />
                      Clear filters
                    </Button>
                  )}
                  <Button asChild>
                    <Link to="/assets/new">
                      <Plus className="h-4 w-4 mr-2" />
                      Add your first asset
                    </Link>
                  </Button>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      {status !== 'deleted' && isAdmin && (
                        <Checkbox
                          checked={selectedIds.length === assets.length && assets.length > 0}
                          onCheckedChange={handleSelectAll}
                        />
                      )}
                    </TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead>Serial Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Service Life</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead>Length</TableHead>
                    <TableHead>Next Inspection</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assets.map((asset) => (
                    <AssetRow
                      key={asset.id}
                      asset={asset}
                      isSelected={selectedIds.includes(asset.id)}
                      onSelect={handleSelectOne}
                      canSelect={status !== 'deleted' && isAdmin}
                      orgToday={orgToday}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {viewMode === 'list' && totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={bulkStatusDialogOpen} onOpenChange={setBulkStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              Change the status of {selectedIds.length} selected asset
              {selectedIds.length > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>New Status</Label>
            <Select
              value={selectedBulkStatus}
              onValueChange={(value) => setSelectedBulkStatus(value as AssetStatus)}
            >
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkStatusUpdate} disabled={bulkUpdateMutation.isPending}>
              {bulkUpdateMutation.isPending ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Assets</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.length} asset
              {selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleScanResult}
        title="Scan Asset"
        description="Scan a barcode to find an asset by serial number"
      />

      <Dialog open={scanNotFoundDialogOpen} onOpenChange={setScanNotFoundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asset Not Found</DialogTitle>
            <DialogDescription>
              No asset found with serial number &quot;{scannedSerial}&quot;. Would you like to create a new asset with this serial number?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanNotFoundDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateFromScan}>
              Create New Asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface AssetRowProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: (id: number, checked: boolean) => void;
  canSelect: boolean;
  orgToday: string;
}

function AssetRow({ asset, isSelected, onSelect, canSelect, orgToday }: AssetRowProps) {
  const isOverdue =
    asset.next_inspection_due && asset.next_inspection_due.slice(0, 10) < orgToday;
  const { canEditAssets, canDeleteAssets } = usePermissions();
  const restoreMutation = useRestoreAsset();
  const unretireMutation = useUnretireAsset();

  const handleRestore = async () => {
    try {
      if (asset.is_deleted) {
        await restoreMutation.mutateAsync(asset.id);
      } else {
        await unretireMutation.mutateAsync(asset.id);
      }
      toast.success(asset.is_deleted ? 'Asset restored' : 'Asset returned to service');
    } catch (error) {
      toast.error(handleApiError(error).message);
    }
  };

  return (
    <TableRow>
      <TableCell>
        {canSelect && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onSelect(asset.id, checked as boolean)}
          />
        )}
      </TableCell>
      <TableCell>{asset.display_name}</TableCell>
      <TableCell>
        <Link
          to={asset.is_deleted ? '#' : `/assets/${asset.id}`}
          className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
          onClick={(event) => {
            if (asset.is_deleted) event.preventDefault();
          }}
        >
          {asset.serial_number}
        </Link>
      </TableCell>
      <TableCell>
        <Badge className={asset.is_deleted ? 'bg-gray-100 text-gray-800' : statusColors[asset.status]}>
          {asset.is_deleted ? 'deleted' : asset.status}
        </Badge>
      </TableCell>
      <TableCell>
        {asset.service_life_status ? (
          <Badge className={serviceLifeColors[asset.service_life_status]}>
            {asset.service_life_status}
          </Badge>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>{asset.manufacturer || '-'}</TableCell>
      <TableCell>{asset.length_m ? `${asset.length_m}m` : '-'}</TableCell>
      <TableCell>
        {asset.next_inspection_due ? (
          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
            {formatDateOnly(asset.next_inspection_due)}
            {isOverdue && ' (Overdue)'}
          </span>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {!asset.is_deleted && (
              <DropdownMenuItem asChild>
                <Link to={`/assets/${asset.id}`}>View Details</Link>
              </DropdownMenuItem>
            )}
            {canEditAssets && !asset.is_deleted && asset.status !== 'retired' && (
              <DropdownMenuItem asChild>
                <Link to={`/assets/${asset.id}/edit`}>Edit</Link>
              </DropdownMenuItem>
            )}
            {canEditAssets && !asset.is_deleted && asset.status !== 'retired' && (
              <DropdownMenuItem asChild>
                <Link to={`/assets/${asset.id}?action=retire`}>
                  <Archive className="h-4 w-4 mr-2" />
                  Retire
                </Link>
              </DropdownMenuItem>
            )}
            {canEditAssets && (asset.is_deleted || asset.status === 'retired') && (
              <DropdownMenuItem
                onClick={handleRestore}
                disabled={restoreMutation.isPending || unretireMutation.isPending}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Restore Asset
              </DropdownMenuItem>
            )}
            {canDeleteAssets && !asset.is_deleted && (
              <DropdownMenuItem className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
